<template>
  <v-select
    v-bind="$attrs"
    :components="{ Deselect }"
    :value="value"
    :options="options"
    :clearable="clearable"
    :disabled="disabled"
    :placeholder="placeholder"
    :searchable="searchable"
    :filterable="filterable"
    :loading="loading"
    :class="classes"
    :get-option-key="getOptionKey"
    :label="optionLabelKey"
    :reduce="reduce"
    @search="onSearch"
    @input="onInput"
    @search:focus="onFocus"
  >
    <template #open-indicator="{ attributes }">
      <span v-bind="attributes">▾</span>
    </template>
    <template #no-options>
      {{ noDataText }}
    </template>
  </v-select>
</template>

<script>
import VSelect from 'vue-select';
import i18n from '@/translations/i18n';

export default {
  name: 'BaseSelect',

  components: {
    VSelect,
  },

  props: {
    value: {
      type: [String, Number, Boolean, Object],
      default: '',
    },
    options: {
      type: Array,
      required: true,
    },
    classes: {
      type: String,
      default: '',
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: '',
    },
    searchable: {
      type: Boolean,
      default: false,
    },
    filterable: {
      type: Boolean,
      default: false,
    },
    debounceTime: {
      // for when filtering is done on backend, don't send requests on every keyup
      type: Number,
      default: 500,
    },
    minSearchSymbols: {
      type: Number,
      default: 2,
    },
    searchFunction: {
      type: Function,
      default: null,
    },
    optionLabelKey: {
      type: String,
      default: 'name',
    },
    optionValueKey: {
      type: String,
      default: 'code',
    },
    returnObject: {
      type: Boolean,
      default: false,
    },
    noDataText: {
      type: String,
      default: i18n.t('common.noData'),
    },
    reduce: {
      // When working with objects, the reduce prop allows you to transform a given object to
      // only the information you want passed to a v-model binding or @input event.
      type: Function,
      default: (option) => option,
    },
  },

  data: () => ({
    Deselect: {
      render: (createElement) =>
        // v-select doesn't expose a slot for clear icon, only a components prop
        // (https://vue-select.org/guide/components.html#deselect)
        // this is used to ensure that clearable icon on BaseSelect looks the same as on BaseDatepicker.
        // Might be a good idea to extract this to it's own component.
        createElement(
          'svg',
          {
            attrs: {
              xmlns: 'http://www.w3.org/2000/svg',
              viewBox: '0 0 1024 1024',
              height: '20px',
              width: '20px',
            },
          },
          [
            createElement('path', {
              attrs: {
                d:
                  'M810.005333 274.005333l-237.994667 237.994667 237.994667 237.994667-60.010667 60.010667-237.994667-237.994667-237.994667 237.994667-60.010667-60.010667 237.994667-237.994667-237.994667-237.994667 60.010667-60.010667 237.994667 237.994667 237.994667-237.994667z',
              },
            }),
          ]
        ),
    },
    loading: false,
    optionsLoaded: false,
    searchString: '',
    debounceTimerId: null,
  }),

  methods: {
    getOptionKey(option) {
      if (typeof option === 'object') {
        return option[this.optionValueKey];
      }
      return option;
    },

    async onFocus() {
      if (!this.searchFunction || this.optionsLoaded) {
        return;
      }

      this.optionsLoaded = true;
      this.loading = true;
      await this.searchFunction('');
      this.loading = false;
    },

    onInput(event) {
      if (!event) {
        this.optionsLoaded = false;
      }

      this.$emit('input', event);
    },

    onSearch(value) {
      this.searchString = value;
      clearTimeout(this.debounceTimerId);
      if (value?.length < this.minSearchSymbols && value?.length > 0) {
        return;
      }
      this.debounceTimerId = setTimeout(async () => {
        if (this.searchFunction) {
          this.loading = true;
          await this.searchFunction(value);
          this.loading = false;
        } else {
          this.$emit('search', value);
        }
      }, this.debounceTime);
    },
  },
};
</script>

<style lang="scss">
@import '../../../node_modules/vue-select/dist/vue-select.css';

.v-select {
  line-height: 30px;
  background-color: white;
}

.v-select .vs__search::placeholder,
.v-select .vs__dropdown-toggle,
.v-select .vs__dropdown-menu {
  border-radius: 0;
}

.v-select .vs__dropdown-menu {
  margin-top: 1px;
  padding: 0;
}

.v-select .vs__dropdown-option--highlight {
  background: #eceeef;
  color: black;
}

.v-select .vs__dropdown-option {
  padding: 10px 15px;
  white-space: normal;
}

.v-select .vs__selected-options {
  padding: 4px 2px;
  flex-wrap: initial;
}

.v-select .vs--disabled .vs__clear,
.vs--disabled .vs__dropdown-toggle,
.vs--disabled .vs__open-indicator,
.vs--disabled .vs__search,
.vs--disabled .vs__selected {
  background-color: #eee;
}

.v-select .vs__dropdown-toggle {
  border-color: #d9dee0 !important;
  padding: 0 8px 4px;
}

.v-select .vs__clear {
  height: 20px;
  fill: rgba(0, 0, 0, 0.5);
}

.v-select .vs__clear:hover {
  fill: rgba(0, 0, 0, 0.8);
}

.vs--disabled,
.vs--disabled * {
  cursor: not-allowed !important;
}

@media screen and (max-width: 767px) {
  .v-select {
    width: 100%;
  }
}
</style>
